import React, { useState } from 'react';

const STEP_LABELS = {
    cognitoUsers: 'Cognito user accounts',
    project: 'Project record',
    tasks: 'Tasks',
    comments: 'Comments',
    wiki: 'Wiki pages',
    members: 'Team members',
    risks: 'Risks',
    issues: 'Issues',
    lead: 'Lead record'
};

const STEP_ORDER = ['cognitoUsers', 'project', 'tasks', 'comments', 'wiki', 'members', 'risks', 'issues', 'lead'];

const DeletionProgressPane = ({ isOpen, clientName, results, isComplete, onClose }) => {
    const [expandedErrors, setExpandedErrors] = useState({});

    if (!isOpen) return null;

    const toggleError = (key) => {
        setExpandedErrors(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const hasErrors = results && Object.values(results).some(r => r.status === 'failed');

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
                return <span className="text-green-600 text-xl">&#x2713;</span>;
            case 'failed':
                return <span className="text-red-600 text-xl">&#x2717;</span>;
            case 'skipped':
                return <span className="text-gray-400 text-xl">&#x2013;</span>;
            case 'pending':
            default:
                return (
                    <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                );
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                {/* Header */}
                <div className={`px-6 py-4 rounded-t-lg ${hasErrors ? 'bg-orange-600' : 'bg-blue-600'} text-white`}>
                    <h2 className="text-xl font-bold">
                        {isComplete
                            ? (hasErrors ? 'Deletion Completed with Errors' : 'Deletion Complete')
                            : `Deleting: ${clientName}`
                        }
                    </h2>
                </div>

                <div className="p-6">
                    {/* Progress list */}
                    <div className="space-y-3">
                        {STEP_ORDER.map(key => {
                            const result = results?.[key] || { status: 'pending', count: 0, error: null };
                            const label = STEP_LABELS[key];

                            return (
                                <div key={key}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-6 flex justify-center">
                                                {getStatusIcon(result.status)}
                                            </div>
                                            <span className={`${result.status === 'failed' ? 'text-red-700' : 'text-gray-700'}`}>
                                                {label}
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            {result.status === 'success' && result.count > 0 && (
                                                <span className="text-sm text-gray-500">({result.count})</span>
                                            )}
                                            {result.status === 'failed' && result.error && (
                                                <button
                                                    onClick={() => toggleError(key)}
                                                    className="text-sm text-red-600 hover:text-red-800"
                                                >
                                                    {expandedErrors[key] ? 'Hide' : 'Details'}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Error details */}
                                    {expandedErrors[key] && result.error && (
                                        <div className="ml-9 mt-1 p-2 bg-red-50 rounded text-sm text-red-700 font-mono">
                                            {result.error}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    {isComplete && (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                            {hasErrors && (
                                <p className="text-sm text-orange-700 mb-4">
                                    Some items could not be deleted. You may need to manually clean up the remaining data.
                                </p>
                            )}
                            <div className="flex justify-end">
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeletionProgressPane;
